/**
 * urgency.js
 *
 * computeUrgency(contact, demos, trials) → { score, signals[] }
 *
 * Additive urgency score — drives Today tab card order and border colour.
 *
 * Signal weights:
 *   Demo today/tomorrow               +50
 *   Trial renewal within 7 days       +40
 *   Trial health = red                +30
 *   Trial health = amber              +15
 *   New BigDough email opened (≤3d)   +20
 *   New BigDough email clicked (≤3d)  +25
 *   Last touch > 21 days (warm→cold)  +10
 *   Last touch > 60 days              +20
 *   No touch ever                     +15
 *
 * Border colour thresholds:
 *   score ≥ 60  → red
 *   score ≥ 30  → amber
 *   otherwise   → none
 */

import { computeHealth } from './health.js';

const MS_PER_DAY = 86_400_000;

function daysDiff(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (isNaN(d)) return Infinity;
  return (Date.now() - d.getTime()) / MS_PER_DAY;
}

export function computeUrgency(contact, demos = [], trials = [], outreachLog = [], settings = {}) {
  const urgencyRedScore    = Number(settings.urgencyRedScore    ?? 60);
  const urgencyAmberScore  = Number(settings.urgencyAmberScore  ?? 30);
  const nextStepOverdueDays = Number(settings.nextStepOverdueDays ?? 3);
  const staleDays1         = Number(settings.staleDays1         ?? 21);
  const staleDays2         = Number(settings.staleDays2         ?? 60);

  const contactId = contact.contact_id ?? '';
  let score = 0;
  const signals = [];

  // Filter related rows by contact_id (matches actual sheet schema)
  const cDemos    = demos.filter((d) => d.contact_id === contactId);
  const cTrials   = trials.filter((t) => t.contact_id === contactId);
  const cOutreach = outreachLog.filter((r) => r.contact_id === contactId);

  // ── Overdue next-step signal ─────────────────────────────────────────────
  const todayIso = new Date().toISOString().slice(0, 10);
  const overdueDemo = cDemos.find((d) => {
    if ((d.status ?? '').toLowerCase() === 'cancelled') return false;
    const due = (d.next_step_due ?? '').slice(0, 10);
    return due && due < todayIso;
  });
  if (overdueDemo) {
    const daysOverdue = Math.floor(daysDiff(overdueDemo.next_step_due));
    if (daysOverdue > nextStepOverdueDays) {
      score += 45;
      signals.push({ label: `Next step overdue ${daysOverdue}d`, weight: 45 });
    } else {
      score += 35;
      signals.push({ label: 'Next step overdue', weight: 35 });
    }
  }

  // ── Demo signals ────────────────────────────────────────────────────────────
  const upcomingDemo = cDemos.find((d) => (d.status ?? '').toLowerCase() === 'scheduled');
  if (upcomingDemo) {
    const daysUntil = -daysDiff(upcomingDemo.scheduled_date ?? upcomingDemo.date ?? '');
    if (daysUntil >= -1 && daysUntil <= 1) {
      score += 50;
      signals.push({ label: 'Demo today/tomorrow', weight: 50 });
    }
  }

  // ── Trial signals ────────────────────────────────────────────────────────────
  const activeTrial = cTrials.find((t) => (t.status ?? '').toLowerCase() === 'active');
  if (activeTrial) {
    const daysUntilEnd = -daysDiff(activeTrial.end_date ?? '');
    if (isFinite(daysUntilEnd) && daysUntilEnd <= 7 && daysUntilEnd >= 0) {
      score += 40;
      signals.push({ label: `Trial ends in ${Math.round(daysUntilEnd)}d`, weight: 40 });
    }
    const health = computeHealth(activeTrial);
    if (health.score === 'red') {
      score += 30;
      signals.push({ label: 'Trial health: Churning', weight: 30 });
    } else if (health.score === 'amber') {
      score += 15;
      signals.push({ label: 'Trial health: At Risk', weight: 15 });
    }
  }

  // ── BigDough engagement signals ─────────────────────────────────────────────
  // Only count outreach events from last 3 days
  const recentOutreach = cOutreach.filter((r) => daysDiff(r.date) <= 3);
  const anyClicked = recentOutreach.some((r) => r.clicked === 'True' || r.clicked === true);
  const anyOpened  = recentOutreach.some((r) => r.opened  === 'True' || r.opened  === true);

  if (anyClicked) {
    score += 25;
    signals.push({ label: 'Email clicked (last 3d)', weight: 25 });
  } else if (anyOpened) {
    score += 20;
    signals.push({ label: 'Email opened (last 3d)', weight: 20 });
  }

  // ── Touch recency signals — derived from outreach log ─────────────────────
  // (last_touch_date doesn't exist in the Contacts sheet; compute from log)
  const lastTouchMs = cOutreach
    .map((r) => new Date(r.date || 0).getTime())
    .filter((t) => !isNaN(t) && t > 0)
    .reduce((max, t) => Math.max(max, t), 0);

  if (lastTouchMs === 0) {
    score += 15;
    signals.push({ label: 'Never contacted', weight: 15 });
  } else {
    const days = (Date.now() - lastTouchMs) / MS_PER_DAY;
    if (days > staleDays2) {
      score += 20;
      signals.push({ label: `Last touch >${staleDays2}d ago`, weight: 20 });
    } else if (days > staleDays1) {
      score += 10;
      signals.push({ label: `Last touch >${staleDays1}d ago`, weight: 10 });
    }
  }

  // ── Border colour ────────────────────────────────────────────────────────────
  let borderColor = 'none';
  if (score >= urgencyRedScore) borderColor = 'red';
  else if (score >= urgencyAmberScore) borderColor = 'amber';

  return { score, signals, borderColor };
}
